import { colors, fonts } from './variables';

// THEME
const theme = {
	// Color Palette
	palette: {
		common: {
			black: colors.black,
			white: colors.white,
		},
		background: {
			paper: colors.white,
			default: colors.greyScale.ligth,
		},
		primary: {
			light: colors.brandColor,
			main: colors.brandColor,
			dark: colors.brandSecondaryColor,
			contrastText: colors.white,
		},
		// Dark background
		secondary: {
			light: colors.white,
			main: colors.white,
			dark: colors.white, // hover
			contrastText: colors.brandColor,
		},
		error: {
			light: colors.error,
			main: colors.error,
			dark: colors.error, // hover
			contrastText: colors.white,
		},
		confirm: {
			light: colors.success,
			main: colors.success,
			dark: colors.success, // hover
			contrastText: colors.white,
		},
		info: {
			light: colors.info,
			main: colors.info,
			dark: colors.info, // hover
			contrastText: colors.white,
		},
		warning: {
			light: colors.warning,
			main: colors.warning,
			dark: colors.warning, // hover
			contrastText: colors.white,
		},
		text: {
			primary: colors.black,
			secondary: `rgba(${colors.black}, 0.5)`,
			disabled: `rgba(${colors.black}, 0.4)`,
			hint: `rgba(${colors.black}, 0.4)`,
		},
	},
	// Breakpoints
	breakpoints: {
		values: {
			xs: 0,
			sm: 480,
			md: 768,
			lg: 1024,
			xl: 1440,
		},
	},
	// Typography
	typography: {
		fontFamily: fonts.fontFamily,
		fontSize: 18,
		textTransform: 'initial',
		h1: {
			fontSize: '4.8rem',
			fontWeight: '500',
			lineHeight: '5.1rem',
			letterSpacing: '0.1rem',
		},
		h2: {
			fontSize: '4rem',
			fontWeight: '500',
			lineHeight: '4.3rem',
			letterSpacing: '0.1rem',
		},
		h3: {
			fontSize: '3.6rem',
			fontWeight: '500',
			lineHeight: '3.8rem',
			letterSpacing: '0.1rem',
		},
		h4: {
			fontSize: '2.8rem',
			lineHeight: '2.9rem',
			fontWeight: 'normal',
			letterSpacing: '0',
		},
		h5: {
			fontSize: '2.4rem',
			lineHeight: '2.6rem',
			fontWeight: 'normal',
			letterSpacing: '0.1rem',
		},
		h6: {
			fontSize: '1.8rem',
			lineHeight: '2.2rem',
			fontWeight: 'normal',
			letterSpacing: '0.1rem',
		},
		subtitle1: {
			fontSize: '1.8rem',
			lineHeight: '2.2rem',
			fontWeight: 'normal',
			letterSpacing: '0.1rem',
		},
		subtitle2: {
			letterSpacing: '0.1rem',
			fontSize: '1.6rem',
			lineHeight: '2.2rem',
			fontWeight: 'normal',
		},
		body1: {
			letterSpacing: '0.1rem',
			fontSize: '1.8rem',
			lineHeight: '1.9rem',
			fontWeight: 'normal',
		},
		body2: {
			letterSpacing: '0.1rem',
			fontSize: '1.2rem',
			lineHeight: '1.2rem',
			fontWeight: 'normal',
		},
		caption: {
			color: colors.black,
			fontSize: '1.4rem',
			letterSpacing: '0.1rem',
			lineHeight: '1.5rem',
		},
		overline: {
			fontSize: '1.6rem',
			letterSpacing: '0.1rem',
			lineHeight: '1.7rem',
		},
		subheading: {
			color: colors.black,
			fontSize: '1.8rem',
			lineHeight: '1.9rem',
		},
	},
	components: {
		// Typography
		MuiTypography: {
			styleOverrides: {
				root: {
					textTransform: 'initial',
				},
			},
		},
		// Button
		MuiButtonBase: {
			defaultProps: {
				disableRipple: true, // ripple animation disabled
			},
		},
		// Button
		MuiButton: {
			defaultProps: {
				disableRipple: true, // ripple animation disabled
			},
			styleOverrides: {
				// Button Primary
				root: {
					borderRadius: '0.4rem',
					boxShadow: 'none',
					fontSize: '1.6rem',
					fontWeight: '600',
					height: '4rem',
					letterSpacing: '0.2rem',
					lineHeight: '1.7rem',
					margin: '0',
					minWidth: '18rem',
					padding: '0 2rem',
					pointer: 'cursor',
					width: 'auto',

					'&.Mui-disabled': {
						backgroundColor: colors.brandColor,
						color: colors.white,
						opacity: '0.3',
					},

					'&:hover': {
						boxShadow: 'none',
					},

					'&:active': {
						boxShadow: 'none',
					},
				},

				// Button Secondary
				outlinedPrimary: {
					borderColor: colors.brandColor,

					'&:hover': {
						backgroundColor: 'transparent',
						border: `.1rem solid ${colors.brandSecondaryColor}`,
						color: colors.brandSecondaryColor,
					},

					'&:active': {
						boxShadow: 'none',
					},

					'&$disabled': {
						color: colors.brandColor,
						opacity: '0.3',
					},
				},
				// Anchor
				text: {
					color: colors.brandColor,
					fontWeight: '600',
					letterSpacing: '0.2rem',
					lineHeight: '1.7rem',
					minWidth: 'unset',
					height: 'auto',
					padding: '0',
					textDecoration: 'underline',
					textTransform: 'unset',

					'&:hover': {
						backgroundColor: 'transparent',
						color: colors.brandSecondaryColor,
						textDecoration: 'underline',
						textTransform: 'unset',
					},

					'&.Mui-disabled': {
						backgroundColor: 'transparent',
						color: colors.brandColor,
						opacity: '0.3',
						textDecoration: 'none',
						textTransform: 'unset',
					},
				},
				// Anchor Primary
				textPrimary: {
					padding: '0',
					textDecoration: 'none',
					textTransform: 'unset',

					'&:hover': {
						backgroundColor: 'transparent',
						color: colors.brandSecondaryColor,
						textDecoration: 'none',
						textTransform: 'unset',
					},

					'&$disabled': {
						color: colors.brandColor,
						opacity: '0.3',
						textDecoration: 'none',
						textTransform: 'unset',
					},
				},
				// Anchor Secondary
				textSecondary: {
					color: colors.white,
					padding: '0',
					textDecoration: 'none',
					textTransform: 'unset',
					'&:hover': {
						backgroundColor: 'transparent',
						color: colors.brandSecondaryColor,
						textDecoration: 'none',
						textTransform: 'unset',
					},
					'&$disabled': {
						color: colors.brandColor,
						opacity: '0.3',
						textDecoration: 'none',
						textTransform: 'unset',
					},
				},
				startIcon: {
					alignItems: 'center',
					display: 'flex',
					height: '2rem',
					justifyContent: 'center',
					margin: '0 1rem 0 0',
					width: '2rem',
				},
			},
		},
		// Alert Badge
		MuiAlert: {
			styleOverrides: {
				root: {
					alignItems: 'center',
					borderRadius: '0.8rem',
					boxShadow: '0 .4rem .8rem rgba(0, 0, 0, 0.2)',
					margin: '0 0 5rem',
					maxWidth: '144rem',
					minHeight: '5rem',
					padding: '1.5rem 2rem',
					width: '100%',
				},
				icon: {
					height: '3rem',
					margin: '0 2rem 0 0',
					padding: 0,
					width: '3rem',
				},
				message: {
					fontSize: '1.8rem',
					lineHeight: '1.5rem',
					fontWeight: '500',
					margin: 0,
					overflow: 'initial',
					padding: 0,
					width: '100%',
				},
				filledInfo: {
					backgroundColor: colors.info,
				},
				filledWarning: {
					backgroundColor: colors.warning,
				},
				filledError: {
					backgroundColor: colors.error,
				},
				filledSuccess: {
					backgroundColor: colors.success,
				},
			},
		},
		// Dialog Dark Overlay
		MuiBackdrop: {
			styleOverrides: {
				root: {
					backgroundColor: `transparent`,
				},
			},
		},
		// Dialog
		MuiDialog: {
			defaultProps: {
				disablebackdropclick: 'true', // prevent close on click out
			},
			styleOverrides: {
				paper: {
					backgroundColor: colors.white,
					borderRadius: '0.8rem',
					minHeight: '49rem',
					padding: '8rem 3rem 0',
				},
				paperWidthSm: {
					maxWidth: 'unset',
					width: '56rem',
				},
				paperScrollPaper: {
					maxHeight: 'unset',
					minHeight: '56rem',
				},
				paperFullScreen: {
					padding: '0',
				},
			},
		},
		// Paper
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundColor: colors.white,
					position: 'relative',
				},
				rounded: {
					borderRadius: '0.2rem',
				},
				elevation24: {
					boxShadow: `.4rem .8rem 1.4rem 0 rgba(0, 0, 0, 0.5)`,
				},
			},
		},
		// Dialog title
		MuiDialogTitle: {
			styleOverrides: {
				root: {
					color: colors.black,
					fontSize: '2.4rem !important',
					fontWeight: 500,
					margin: '0 0 3rem !important',
					padding: '0 2rem !important',
					textAlign: 'center',
				},
			},
		},
		// Dialog content
		MuiDialogContent: {
			styleOverrides: {
				root: {
					fontSize: '1.8rem',
					padding: '0 2rem !important',
					textAlign: 'center',
					overflow: 'unset',
				},
			},
		},
		// Dialog content text
		MuiDialogContentText: {
			styleOverrides: {
				root: {
					textAlign: 'center',
				},
			},
		},
		// Dialog actions
		MuiDialogActions: {
			styleOverrides: {
				root: {
					justifyContent: 'center',
					margin: '4rem 0',
					padding: '0 !important',
				},
			},
		},
		// Appbar
		MuiAppBar: {
			styleOverrides: {
				root: {
					alignItems: 'center',
					backgroundColor: colors.brandColor,
					boxShadow: 'none',
					justifyContent: 'center',
					minHeight: '10rem',
					padding: '0 4rem',
					zIndex: 2,
				},
				colorDefault: {
					boxShadow: 'none',
					backgroundColor: colors.white,
					minHeight: '7.2rem',
					width: '100%',
					height: '10rem',
					padding: '0 6.5rem',
					justifyContent: 'center',
				},
			},
		},
		// Toolbar
		MuiToolbar: {
			defaultProps: {
				disableGutters: true,
			},
			styleOverrides: {
				regular: {
					height: '100%',
					justifyContent: 'space-between',
					margin: '0 auto',
					padding: '0 !important',
					width: '100%',
				},
			},
		},
		// List item
		MuiListItemText: {
			styleOverrides: {
				root: {
					fontSize: '1.6rem',
					fontWeight: 500,
					padding: 0,
					margin: 0,
				},
			},
		},
		// Form Control
		MuiFormControl: {
			styleOverrides: {
				root: {
					maxWidth: '31rem',
					minWidth: '100%',
					textAlign: 'left',
					width: '100%',
				},
			},
		},
		// Input base
		MuiInputBase: {
			defaultProps: {
				disableUnderline: true,
			},
			styleOverrides: {
				root: {
					backgroundColor: colors.white,
					border: 0,
					borderRadius: '0.2rem !important',
					boxShadow: '0 0.4rem 0.8rem 0 rgba(0, 0, 0, 0.2)',
					boxSizing: 'border-box',
					color: colors.black,
					height: '5rem',
					fontSize: '1.8rem',
					margin: 0,
					maxWidth: '100%',
					outline: 'none',
					padding: 0,
					position: 'relative',
					width: '100%',
				},
				input: {
					alignItems: 'center',
					border: '.2rem solid transparent', // avoid wrong animation
					boxSizing: 'border-box',
					color: 'unset',
					display: 'flex',
					padding: '1.5rem',
					fontSize: '1.8rem',
					height: '5rem',
					zIndex: '1', // SUPER IMPORTANT, Don't Touch!
				},
				formControl: {
					padding: '0',
				},
				secondary: {
					root: {
						border: '.2rem solid lime',
					},
				},
			},
		},
		// Input outlined
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					'&.Mui-error': {
						border: `0.2rem solid ${colors.error}`, // box-input error
					},
				},
				input: {
					color: colors.black,
					fontSize: '1.8rem',
					fontWeight: '500',
					lineHeight: '1.9rem',
				},
				notchedOutline: {
					border: 0,
				},
			},
		},
		// FORM LABEL
		MuiFormLabel: {
			styleOverrides: {
				root: {
					color: colors.black,
					fontSize: '1.4rem !important',
					fontWeight: 'normal !important',
					zIndex: '20',
				},
				filled: {
					color: 'black !important',
					fontWeight: 'normal !important',
					transform: 'translate(0, -.3rem) !important',
				},
			},
		},
		// Input label
		MuiInputLabel: {
			styleOverrides: {
				root: {
					color: 'rgba(0, 0, 0, 0.38) !important',
					fontSize: '1.8rem !important',
					fontWeight: '500 !important',
					letterSpacing: 'inherit',
					lineHeight: '1.7rem',
					paddingLeft: '1.5rem !important',
					pointerEvents: 'none',
					position: 'relative',
					top: '.2rem',
					zIndex: 1,

					'&:hover': {
						color: colors.black,
						outline: 'none',
						padding: '0',
					},
					'&:focus': {
						outline: 'none',
						padding: '0',
					},
					'&.Mui-focused': {
						color: `${colors.black} !important`,
						fontWeight: 'normal !important',
						transform: 'translate(0, -0.3rem)',
					},
				},
				formControl: {
					margin: '0',
					transform: 'translate(0, 3.2rem)',
					zIndex: '1',
				},
				shrink: {
					color: `${colors.black} !important`,
					fontSize: '1.4rem !important',
					fontWeight: 'normal !important',
					padding: '0 !important',
					transform: 'translate(0, 3.2rem)',
				},
				animated: {
					transition: '0.3s',

					'&:focus': {
						transition: 'top 0.3s linear, left 0.3s linear, font-size 0.3s linear',
					},
				},
			},
		},
		// Select
		MuiSelect: {
			defaultProps: {
				disableUnderline: true,
			},
			styleOverrides: {
				input: {
					backgroundColor: colors.white,
					maxWidth: '100%',
					width: '100%',
				},
				select: {
					backgroundColor: 'transparent',
					display: 'block',
					fontWeight: '500',
					height: 'auto',
					maxWidth: '100%',
					padding: '0 1.5rem !important',
					width: '100%',

					'&:error': {
						backgroundColor: 'transparent',
						outline: 'none',
					},
					'&:focus': {
						backgroundColor: 'transparent',
						outline: 'none',
					},
					'&:selected': {
						backgroundColor: 'transparent',
						outline: 'none',
					},
					'&.Mui-selected': {
						backgroundColor: colors.white,
						'&:hover': {
							backgroundColor: 'transparent',
						},
					},
					'&.Mui-focusVisible': {
						backgroundColor: 'transparent',
					},
				},
				selectMenu: {
					alignItems: 'center',
					border: '0',
					display: 'block',
					height: '5rem',
					lineHeight: '5rem',
					textAlign: 'left',
					width: '23.3rem',
				},
				icon: {
					display: 'none',
				},
				iconOpen: {
					transform: 'rotate(180deg)',
				},
			},
		},
		// Form Helper Text
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					color: colors.black,
					fontSize: '1.4rem',
					fontWeight: '600',
					letterSpacing: '0',
					lineHeight: '1.5rem',
					margin: '0',
					padding: '0',
					position: 'absolute',
					top: '7rem',

					'&.Mui-error': {
						color: colors.error,
					},
				},
			},
		},

		// Form control label
		MuiFormControlLabel: {
			styleOverrides: {
				root: {
					margin: '0',

					'&$disabled': {
						color: 'rgba(255, 255, 255, 0.5)',
					},
				},
			},
		},
		// List
		MuiList: {
			styleOverrides: {
				root: {
					backgroundColor: colors.white,
					margin: ' 0',
					maxWidth: '31rem',
					padding: '2rem 1.5rem !important',
					width: '100%',

					'&:focus': {
						backgroundColor: 'transparent',
					},
				},
			},
		},

		// List Item Dropdown
		MuiListItem: {
			styleOverrides: {
				root: {
					color: colors.black,
					fontSize: '1.8rem !important',
					lineHeight: 'normal !important',
					minHeight: '3.5rem !important',
					margin: '10rem 1.5rem !important',
					padding: '1.5rem',
					whiteSpace: 'pre-line !important',

					'&$selected': {
						fontWeight: '600 !important',
						color: '#00b8de !important',
						backgroundColor: '#fff',
					},
					'&:hover': {
						backgroundColor: 'transparent !important', // avoid styleguide
						fontWeight: '500',
					},
				},
				button: {
					'&:focus': {
						backgroundColor: 'transparent !important',
					},
				},
			},
		},
		// Expansion panel
		MuiExpansionPanel: {
			styleOverrides: {
				root: {
					height: '6rem',
				},
				'&.Mui-expanded': {
					height: '100%',
				},
			},
		},
		// Popover
		MuiPopover: {
			styleOverrides: {
				paper: {
					boxShadow: `0 0.4rem 0.8rem 0 rgba(${colors.black}, 0.2)`,
					margin: '1.5rem 0 0 0',
					maxHeight: '30rem',
				},
			},
		},

		// Switch
		MuiSwitch: {
			styleOverrides: {
				root: {
					height: '1.4rem',
					width: '5.5rem',
				},
				switchBase: {
					height: '2rem',
					width: '4rem',

					'&$disabled': {
						color: '#fff !important',

						'& + $bar': {
							backgroundColor: '#9596af !important',
							opacity: 0.5,
						},
					},
				},
				'&.Mui-checked': {
					transform: 'translateX(2.1rem)',
				},
				bar: {
					backgroundColor: '#9596af',
					borderRadius: '2rem',
					boxShadow: '0 .2rem .6rem 0 rgba(0,0,0,0.2)',
					height: '2rem',
					width: '4rem',
				},
				colorPrimary: {
					color: '#fff',

					'&$checked': {
						color: '#fff',

						'& + $bar': {
							backgroundColor: '#2d32aa',
							opacity: 1,
						},
					},
				},
			},
		},
		// Input adornment (like +39)
		MuiInputAdornment: {
			styleOverrides: {
				root: {
					color: colors.greyScale.medium,
					height: '2.1rem',
					margin: '0',
					width: '4.2rem',
					zIndex: '1',
				},
				positionStart: {
					borderColor: colors.greyScale.medium,
					color: colors.greyScale.medium,
					height: '1.4rem',
					lineHeight: '1.4rem',
					fontSize: '1.8rem',
					borderWidth: '0 .1rem 0 0',
					borderStyle: 'solid',
					padding: '.5rem 0 0',
					transition: 'opacity .3s linear',
					left: '0',
					width: '3rem',
					marginRight: '0',
				},
			},
		},
		// Menu item
		MuiMenuItem: {
			styleOverrides: {
				root: {
					backgroundColor: colors.white,
					fontSize: '1.8rem',
					height: 'auto',
					fontWeight: 'normal !important',
					letterSpacing: 0,
					marginBottom: '1.5rem',
					padding: '0',
					whiteSpace: 'pre-wrap',

					'&:last-child': {
						marginBottom: '0',
					},
					'&:hover': {
						backgroundColor: 'transparent !important',
						fontWeight: '500 !important',
					},
					'&:focus': {
						backgroundColor: 'transparent !important',
					},
					'&.Mui-selected': {
						backgroundColor: 'transparent !important',
					},
				},
			},
		},
		// Linear process bar
		MuiLinearProgress: {
			styleOverrides: {
				determinate: {
					width: '100%',
					borderRadius: '.4rem',
					height: '.6rem',
					maxWidth: '75rem',
				},
				buffer: {
					width: '100%',
					borderRadius: '.5rem',
					height: '1rem',
					maxWidth: '54.5rem',
					backgroundColor: 'transparent',
				},
				bar1Buffer: {
					backgroundColor: '#06EAFF',
					height: '1rem',
				},
				bar2Buffer: {
					backgroundColor: `rgba(${colors.white}, 0.3)`,
				},
				dashedColorPrimary: {
					display: 'none',
				},
				barColorPrimary: {
					backgroundColor: colors.success,
					height: '.6rem',
				},
				barColorSecondary: {
					backgroundColor: colors.white,
					height: '.6rem',
				},
				colorPrimary: {
					backgroundColor: `rgba(${colors.brandColor}, 0.3)`,
				},
				colorSecondary: {
					backgroundColor: `rgba(${colors.white}, 0.3)`,
				},
			},
		},
		// Checkbox
		MuiCheckbox: {
			styleOverrides: {
				root: {
					border: '0',
					borderRadius: '0.3rem',
					backgroundColor: colors.white,
					color: 'trasparent',
					height: '2rem',
					padding: '0',
					width: '2rem',

					'&.Mui-disabled': {
						color: colors.brandColor,
						opacity: '0.3',

						'& svg': {
							height: '3rem',
							width: '3rem',
						},
					},

					'&:hover': {
						color: colors.brandSecondaryColor,
						backgroundColor: colors.white,
					},

					'&$checked': {
						'&:hover': {
							backgroundColor: colors.white,
						},

						'&$disabled': {
							opacity: '0.3',
						},
					},

					'&$disabled': {
						opacity: '0.3',

						'&$checked': {
							color: colors.brandColor,
						},
					},

					'& svg': {
						height: '3rem',
						width: '3rem',
					},

					'& input': {
						height: '2rem',
						width: '2rem',

						'&$disabled': {
							cursor: 'disabled',
						},
					},
				},
			},
		},
		// SLIDER
		MuiSlider: {
			styleOverrides: {
				root: {
					maxWidth: '23.6rem',
					width: '100%',
				},
				track: {
					backgroundColor: `rgba(${colors.white}, .6)`,
					height: '.4rem',
					borderRadius: '.4rem',
				},
				thumb: {
					height: '1.5rem',
					width: '1.5rem',
					border: '.1rem solid #F0F0F0',
					backgroundColor: colors.white,
					boxShadow: '0 .2rem .2rem 0 rgba(0, 0, 0, 0.3)',

					'&:hover': {
						boxShadow: 'none',
					},

					'&$disabled': {
						opacity: '.6',
						height: '1.5rem',
						width: '1.5rem',
						border: '.1rem solid #F0F0F0',
						backgroundColor: colors.white,
						boxShadow: '0 .2rem .2rem 0 rgba(0, 0, 0, 0.3)',
					},
				},
				trackBefore: {
					backgroundColor: '#00b8de',
					borderRadius: '.4rem',
					height: '.4rem',

					'&$disabled': {
						backgroundColor: 'rgba(0, 184, 222, 0.4)',
						borderRadius: '.4rem',
						height: '.4rem',
					},
				},
				trackAfter: {
					backgroundColor: `rgba(${colors.white}, .6)`,
					borderRadius: '.4rem',
					height: '.4rem',

					'&$disabled': {
						backgroundColor: `rgba(${colors.white}, .6)`,
						borderRadius: '.4rem',
						height: '.4rem',
					},
				},
			},
		},
		// Radio button
		MuiRadio: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent',
					border: '0',
					borderRadius: '.2rem',
					color: colors.brandColor,
					height: '2.5rem',
					padding: '0',
					width: '2.5rem',
				},
			},
		},
		// Tooltip
		MuiTooltip: {
			styleOverrides: {
				popper: {
					backgroundColor: colors.white,
					boxShadow: '0 0 .8rem 0 rgba(0, 0, 0, 0.3)',
					borderRadius: '.3rem',
					fontSize: '1.4rem',
					left: '0',
					marginTop: '1rem',
					maxWidth: '30rem',
					opacity: '1',
					right: '0',
					padding: '0',
					width: 'auto',
				},
				// text inside popper
				tooltip: {
					backgroundColor: colors.white,
					borderRadius: '0.8rem',
					color: colors.black,
					fontSize: '1.6rem',
					lineHeight: 'normal',
					margin: '0 !important',
					maxWidth: '30rem',
					padding: '1.5rem',
				},
			},
		},
		// Icon Button
		MuiIconButton: {
			defaultProps: {
				disableRipple: true, // prevent hover animation
			},
			styleOverrides: {
				root: {
					padding: '0',
					'&:hover': {
						backgroundColor: 'transparent',
					},
				},
			},
		},
		// Table Head
		MuiTableHead: {
			styleOverrides: {
				root: {
					backgroundColor: colors.greyScale.ligth,
					// backgroundColor: colors.brandColor,
					minHeight: '4rem',
				},
			},
		},
		// Table row
		MuiTableRow: {
			styleOverrides: {
				root: {
					minHeight: '4rem',
					'&.Mui-selected': {
						backgroundColor: 'rgba(45,50, 170, 0.2) !important',
					},
				},
			},
		},
		// Table cell
		MuiTableCell: {
			styleOverrides: {
				head: {
					color: colors.black,
					fontWeight: '600',
					fontSize: '1.6rem',
					letterSpacing: '0.07rem',
					textAlign: 'left',
					textTransform: 'uppercase',
				},
				root: {
					fontWeight: 'normal',
					fontSize: '1.6rem',
					letterSpacing: '0',
					padding: '1rem 0 1rem 1.5rem',
					textAlign: 'left',
					width: 'min-content',
				},
			},
		},
		// Table pagination
		MuiTablePagination: {
			styleOverrides: {
				selectLabel: {
					fontSize: '1.8rem',
					fontWeight: 'normal',
					lineHeight: '1.9rem',
					letterSpacing: '0',
					textAlign: 'right',
				},
				displayedRows: {
					fontSize: '1.8rem',
					fontWeight: 'normal',
					lineHeight: '1.9rem',
					letterSpacing: '0',
				},
				menuItem: {
					fontSize: '1.8rem',
					fontWeight: 'normal',
					lineHeight: ' 1.9rem',
					letterSpacing: '0',
				},
				actions: {
					marginLeft: '3rem',
				},
			},
		},
		// Native Select
		MuiNativeSelect: {
			styleOverrides: {
				select: {
					'&:focus': {
						backgroundColor: 'transparent',
					},
				},
				icon: {
					fontSize: '2rem',
				},
			},
		},
		// Picker Claendar HEader
		MuiPickersCalendarHeader: {
			styleOverrides: {
				root: {
					alignItems: 'center',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					margin: '0',
					minHeight: '5rem',
					maxHeight: 'unset',
					padding: '0',
					position: 'relative',
				},
				label: {
					fontSize: '1.9rem',
					fontWeight: '500',
					lineHeight: '1.9rem',
					letterSpacing: '0',
					margin: '0',

					'&:hover': {
						color: colors.brandSecondaryColor,
					},
				},
				labelContainer: {
					margin: '0',
					left: '50%',
					position: 'absolute',
					transform: 'translate(-50%,-50%)',
					top: '50%',
					zIndex: '1',
				},
				switchViewButton: {
					display: 'none',
				},
			},
		},
		// Picker Arrow Switcher
		MuiPickersArrowSwitcher: {
			styleOverrides: {
				root: {
					maxWidth: '30rem',
					position: 'absolute',
					transform: 'translateY(-50%)',
					top: '50%',
					width: '100%',
				},
				spacer: {
					width: '100%',
				},
			},
		},
		// Day Picker
		MuiDayPicker: {
			styleOverrides: {
				header: {
					borderBottom: `.1rem solid rgba(${colors.black}, 0.3)`,
					margin: '0 1.9rem .8rem',
				},
				weekDayLabel: {
					color: colors.black,
					fontSize: '1.6rem !important',
					fontWeight: '500 !important',
					lineHeight: '1.7rem',
					letterSpacing: '0',
				},
			},
		},
		// Picker Day
		MuiPickersDay: {
			styleOverrides: {
				root: {
					border: '0!important',
					color: colors.black,
					fontSize: '1.6rem !important',
					fontWeight: '500 !important',
					lineHeight: '1.7rem',
					letterSpacing: '0',

					'&.Mui-selected': {
						backgroundColor: `${colors.brandSecondaryColor} !important`,
					},

					'&:hover': {
						backgroundColor: colors.brandSecondaryColor,
						color: colors.white,
					},
				},
				today: {
					backgroundColor: `${colors.white} !important`,
					border: `0.2rem solid ${colors.brandColor} !important`,
					color: colors.black,

					'&(.Mui-selected)': {
						backgroundColor: `${colors.brandSecondaryColor} !important`,
						border: '0 !important',
					},
					'&:hover': {
						backgroundColor: `${colors.brandSecondaryColor} !important`,
						color: colors.white,
						border: '0 !important',
					},
				},
			},
		},
		// Private Picker Year
		PrivatePickersYear: {
			styleOverrides: {
				button: {
					color: colors.black,
					border: '0!important',
					fontSize: '1.6rem !important',
					fontWeight: '500 !important',
					lineHeight: '1.7rem',
					letterSpacing: '0',

					'&.Mui-selected': {
						backgroundColor: colors.brandSecondaryColor,
						color: colors.white,
					},
					'&:hover': {
						backgroundColor: `${colors.brandSecondaryColor} !important`,
						color: colors.white,
					},
				},
			},
		},
		// Tabs
		MuiTabs: {
			styleOverrides: {
				root: {
					border: 0,
					maxHeight: 'auto',
					height: 'auto',
					minHeight: '0',
				},
				indicator: {
					backgroundColor: colors.brandSecondaryColor,
					height: '0.3rem',
				},
			},
		},
		// Tab
		MuiTab: {
			styleOverrides: {
				root: {
					color: colors.brandColor,
					fontSize: '1.8rem',
					fontWeight: 500,
					lineHeight: '1.9rem',
					height: 'auto',
					margin: '0 2rem 0 0',
					maxHeight: 'unset',
					minHeight: '0',
					minWidth: 'unset !important',
					padding: '0 0 .5rem 0',

					'&.Mui-selected': {
						color: colors.brandSecondaryColor,
					},
				},
			},
		},
		// Skeleton
		MuiSkeleton: {
			styleOverrides: {
				root: {
					backgroundColor: 'rgba(190, 190, 190, .5)',

					'&::after': {
						background: 'linear-gradient(90deg, transparent, rgb(255 255 255 / 50%), transparent)',
					},
				},
			},
		},
		// Autocomplete
		MuiAutocomplete: {
			styleOverrides: {
				root: {
					padding: 0,
				},
				input: {
					height: 'auto',
					padding: '0 !important',
					width: '100%',
				},
				popper: {
					border: 0,
					borderRadius: '0.2rem !important',
					margin: '.5rem 0 0 !important',
				},
				paper: {
					boxShadow: '0 0.4rem 0.8rem 0 rgba(0, 0, 0, 0.2)',
					maxHeight: '30rem',
				},
				noOptions: {
					background: 'transparent',
					color: colors.greyScale.dark,
					fontSize: '1.8rem',
					lineHeight: '1.9rem',
					padding: '1.5rem',
				},
				listbox: {
					overflow: 'hidden',
					padding: '2rem 1.5rem',
				},
				option: {
					backgroundColor: `${colors.white} !important`,
					padding: '0 !important',
				},
			},
		},
	},
};

export default theme;
